import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatSidenav } from '@angular/material/sidenav';
import { SelectionModel } from '@angular/cdk/collections';
import { ScheduledPaymentsService } from '../../../../services/payments/scheduled-payments.service';
import {
  EngagementScheduledPaymentUi,
  ScheduledAutoPaymentUiV4,
  ScheduledPaymentUi,
} from '../models/scheduled-payment-ui';
import {
  PaymentMethodIsNotFromUserComponent,
} from '../../modals/payment-method-is-not-from-user/payment-method-is-not-from-user.component';
import { PaymentMethodType } from '@aiwyn/api';
import { ConfigService } from '@aiwyn/config';
import { Store } from '@ngxs/store';
import { filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { GetScheduledPayments, SettingsState } from '../../settings/state';
import { extractFailureContext, LoadableStatus } from '@aiwyn/shared';
import {
  AbstractPaymentMethod,
  PaymentPlatformService,
  ScheduledPaymentTrackService,
  ScheduledPaymentWorkflowEvent,
} from '@aiwyn/common/payments';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

export type SortingProperty = string;
export type SortingFunction = (item: ScheduledPaymentUi, property: SortingProperty) => string | number;

@Component({
  selector: 'app-scheduled',
  templateUrl: './scheduled.component.html',
  styleUrls: ['./scheduled.component.scss'],
  standalone: false,
  providers: [ScheduledPaymentTrackService],
})
export class ScheduledComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private readonly _unsubscribe$ = new Subject<void>();

  // Sort for material table.
  @ViewChildren(MatSort) sort: QueryList<MatSort>;

  /** paginator for mat table */
  @ViewChildren(MatPaginator) paginator: QueryList<MatPaginator>;

  // Material sidenav.
  @ViewChild('sidenav') public sidenav: MatSidenav;

  // Search string for global search.
  @Input() searchString: string;

  // clientId used to store URL data.
  // Note: URL must use format specified in app-routing.module.ts.
  clientId: string = '';

  // Columns for material table.
  columnsToDisplay = ['date', 'client', 'method', 'amount', 'status', 'paymentPlan', 'scheduledPaymentMenu'];

  // shownTimeSelectionOptions for dropdown table filter.
  shownTimeSelectionOptions = ['Show - All Time', 'Show - Next 3 Months', 'Show - Next 6 Months', 'Show - Next Year'];
  private readonly _selectedTimeOption$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  // Angular SelectionModel for selecting row on click.
  selection = new SelectionModel(true, [], true);

  // Hide unpaid table if there is no data.
  hideScheduledTable$: Observable<boolean> = combineLatest([
    this.store.select(SettingsState.getScheduledPayments),
    this._selectedTimeOption$,
  ]).pipe(
    map(([payments, timeRangeOption]) => timeRangeOption === '' && payments.length === 0),
    startWith(false),
  );

  hasError$ = this.store.select(
    this.configService.paymentPlanV4Enabled ? SettingsState.geV4PaymentsStatus : SettingsState.getV3PaymentsStatus,
  ).pipe(
    map(status => status === LoadableStatus.ERROR),
  );

  scheduledPaymentProcessingComplete = false;

  private readonly scheduledPaymentTrackService = inject(ScheduledPaymentTrackService);

  constructor(
    public dialog: MatDialog,
    public scheduledPaymentService: ScheduledPaymentsService,
    private readonly paymentPlatformService: PaymentPlatformService,
    public configService: ConfigService,
    private cdref: ChangeDetectorRef,
    private readonly store: Store,
    public readonly router: Router,
  ) { }

  ngOnChanges(): void {
    this.applyFilter(this.searchString);
  }

  ngOnInit(): void {
    // Load the Scheduled payments into state (not needed before this point)
    this.store.dispatch(new GetScheduledPayments());

    combineLatest([
      this.paymentPlatformService.getSavedPaymentMethods(),
      this.scheduledPaymentService.scheduledPaymentsLoaded$,
    ]).pipe(
      tap(([savedPaymentMethods]) => {
        this.scheduledPaymentService.dataSourceScheduledPayments.data.forEach(scheduledPayment => {
          const paymentMethodId = scheduledPayment.actionDefInstanceDTO.action.paymentMethodId;
          scheduledPayment.isPaymentMethodFromUser = savedPaymentMethods.some(({ id }) => id === paymentMethodId);
        });

        this.scheduledPaymentProcessingComplete = true;
        // set filter predicate for schedule payments table
        this.scheduledPaymentService.dataSourceScheduledPayments.filterPredicate = (data: ScheduledPaymentUi, filter: string) => {
          return this.scheduledPaymentService.searchFilterFunction(data, filter);
        };
      }),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  ngAfterViewInit(): void {
    combineLatest([
      this.sort.changes.pipe(
        map((sortQueryList: QueryList<MatSort>) => sortQueryList.first),
        startWith(this.sort.first),
        filter<MatSort>(Boolean),
      ),
      this.paginator.changes.pipe(
        map((paginatorQueryList: QueryList<MatPaginator>) => paginatorQueryList.first),
        startWith(this.paginator.first),
        filter<MatPaginator>(Boolean),
      )
    ]).pipe(
      tap(([sort, paginator]) => {
        this.scheduledPaymentService.dataSourceScheduledPayments.sort = sort;
        this.scheduledPaymentService.dataSourceScheduledPayments.sortingDataAccessor = this.sortingFunction();
        this.scheduledPaymentService.dataSourceScheduledPayments.paginator = paginator;
        this.cdref.detectChanges();
      }),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  /**
   * called on ngChanges, handles formatting filter value and setting it
   * @param filterValue
   */
  applyFilter(filterValue: string): void {
    this.scheduledPaymentService.dataSourceScheduledPayments.filter = filterValue ? filterValue.trim().toLowerCase() : '';
  }

  /**
   * Skip scheduled payment.
   */
  skipScheduledPayment(skippedScheduledPayment: ScheduledPaymentUi): void {
    const isAutoPay = skippedScheduledPayment instanceof ScheduledAutoPaymentUiV4;
    const scheduledPaymentId = this._getScheduledPaymentId(skippedScheduledPayment);
    const startEvent = isAutoPay
      ? ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_SKIP_SUBMITTED
      : ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_SKIP_SUBMITTED;
    const succeededEvent = isAutoPay
      ? ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_SKIP_SUCCEEDED
      : ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_SKIP_SUCCEEDED;
    const failedEvent = isAutoPay
      ? ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_SKIP_FAILED
      : ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_SKIP_FAILED;
    const operationKey = `SKIP:${scheduledPaymentId}`;

    this.scheduledPaymentTrackService.trackWorkflow(startEvent, { scheduledPaymentId });
    this.scheduledPaymentTrackService.startOperation(operationKey);

    skippedScheduledPayment.getDeclineRequest(this.store).pipe(
      tap({
        next: _ => this.scheduledPaymentTrackService.trackWorkflow(succeededEvent, {
          scheduledPaymentId,
          operationDurationMs: this.scheduledPaymentTrackService.endOperation(operationKey),
        }),
        error: (error: unknown) => this.scheduledPaymentTrackService.trackWorkflow(failedEvent, {
          ...extractFailureContext(error),
          scheduledPaymentId,
          operationDurationMs: this.scheduledPaymentTrackService.endOperation(operationKey),
        }),
      }),
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  /**
   * Resume scheduled payment.
   */
  resumeScheduledPayment(resumedScheduledPayment: ScheduledPaymentUi): void {
    const isAutoPay = resumedScheduledPayment instanceof ScheduledAutoPaymentUiV4;
    const scheduledPaymentId = this._getScheduledPaymentId(resumedScheduledPayment);
    const startEvent = isAutoPay
      ? ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_RESUME_SUBMITTED
      : ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_RESUME_SUBMITTED;
    const succeededEvent = isAutoPay
      ? ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_RESUME_SUCCEEDED
      : ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_RESUME_SUCCEEDED;
    const failedEvent = isAutoPay
      ? ScheduledPaymentWorkflowEvent.SCHEDULED_AUTO_PAYMENT_RESUME_FAILED
      : ScheduledPaymentWorkflowEvent.SCHEDULED_PAYMENT_RESUME_FAILED;
    const operationKey = `RESUME:${scheduledPaymentId}`;

    this.scheduledPaymentTrackService.trackWorkflow(startEvent, { scheduledPaymentId });
    this.scheduledPaymentTrackService.startOperation(operationKey);

    resumedScheduledPayment.getResumeRequest(this.store).pipe(
      tap({
        next: _ => this.scheduledPaymentTrackService.trackWorkflow(succeededEvent, {
          scheduledPaymentId,
          operationDurationMs: this.scheduledPaymentTrackService.endOperation(operationKey),
        }),
        error: (error: unknown) => this.scheduledPaymentTrackService.trackWorkflow(failedEvent, {
          ...extractFailureContext(error),
          scheduledPaymentId,
          operationDurationMs: this.scheduledPaymentTrackService.endOperation(operationKey),
        }),
      }),
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  /**
   * Resolves the analytics-friendly id from a scheduled payment UI model.
   * V3 uses the action-def-instance id, V4 recurring uses scheduledPaymentId,
   * V4 autopay uses scheduledAutoPaymentId. Always returned as a string.
   */
  private _getScheduledPaymentId(scheduledPayment: ScheduledPaymentUi): string {
    const model = (scheduledPayment as { model?: { scheduledPaymentId?: string; scheduledAutoPaymentId?: string } }).model;
    const id = model?.scheduledAutoPaymentId
      ?? model?.scheduledPaymentId
      ?? scheduledPayment.actionDefInstanceDTO?.id;
    return id != null ? String(id) : '';
  }

  shownTimeSelectionChanged(value: string): void {
    this._selectedTimeOption$.next(value);
    this.store.dispatch(new GetScheduledPayments(value, true));
  }

  /**
   * This is launched when the info icon is clicked to let the user know the payment method is not theirs.
   */
  launchPaymentMethodIsNotFromUserInfoModal(): void {
    this.dialog.open(PaymentMethodIsNotFromUserComponent);
  }

  isDirectDebit(paymentMethodType: PaymentMethodType): boolean {
    return AbstractPaymentMethod.isDirectDebit(paymentMethodType);
  }

  isEngagementScheduledPayment(scheduledPayment: ScheduledPaymentUi): boolean {
    return scheduledPayment instanceof EngagementScheduledPaymentUi;
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  /**
   * Defines sorting parameters.
   * @param item
   * @param property
   */
  sortingFunction(): SortingFunction {
    return function (item: ScheduledPaymentUi, property: SortingProperty) {
      switch (property) {
        case 'date': {
          return new Date(item.actionDefInstanceStaged.scheduledReleaseDate);
        }

        case 'amount': {
          return item.getAmountDisplay();
        }

        case 'client': {
          return item.client.name;
        }

        case 'method': {
          return item.actionDefInstanceDTO.action.cardType + item.actionDefInstanceDTO.action.last4;
        }

        case 'paymentPlan': {
          return item.paymentPlan.name;
        }

        default: {
          return item['paymentPlan'][property];
        }
      }
    }
  }
}
